import { useCallback, useEffect, useState } from "react";
import { CreditCard, Loader2, ShieldCheck, X, Info } from "lucide-react";
import { useToast } from "@/components/ui/toast";
import { api } from "@/lib/api";
import { StripePaymentElement } from "@/components/StripePaymentElement";
import BrandLogo from "@/components/BrandLogo";

interface SavedCard {
  has_card: boolean;
  brand?: string | null;
  last4?: string | null;
  exp_month?: number | null;
  exp_year?: number | null;
}

interface ReplaceCardResult {
  last4: string | null;
  replaced_count: number;
  /** Past-due balance the new card cleared on the spot, in dollars (0 if none). */
  settled_amount: number;
  /** Set when that collection was declined — the card swap still succeeded. */
  settle_error: string | null;
}

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Card on file for the buyer — the Payment Method settings tab.
 *
 *  Exactly ONE card is kept: saving a new one makes it the default for renewals
 *  and one-click unlocks, and the backend detaches the previous card. The form
 *  runs a SetupIntent, so opening this tab and entering a card never charges. */
export function PaymentMethodCard() {
  const toast = useToast();
  const [card, setCard] = useState<SavedCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadCard = useCallback(async () => {
    try {
      setCard(await api.get<SavedCard>("/payments/saved-card"));
    } catch {
      // A gateway hiccup must not blank the tab — fall back to the empty state,
      // which still offers "Add card".
      setCard({ has_card: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadCard(); }, [loadCard]);

  // Deferred: the SetupIntent is minted on the save click, so an abandoned form
  // leaves no incomplete SetupIntents behind in Stripe.
  const createSetupIntent = async () => {
    const res = await api.post<{ client_secret: string }>("/payments/create-card-setup-intent", {});
    return res.client_secret;
  };

  const handleSaved = async (setupIntentId: string) => {
    setSaving(true);
    try {
      const res = await api.post<ReplaceCardResult>(
        "/payments/replace-card", { setup_intent_id: setupIntentId },
      );
      await loadCard();
      setShowModal(false);

      // The card is saved either way — a failed collection is reported without
      // pretending the swap didn't happen.
      if (res.settle_error) {
        toast.error(
          `Card saved, but your outstanding payment didn't go through: ${res.settle_error} ` +
          "Try it again from Subscription Plan → Retry payment.",
        );
      } else if (res.settled_amount > 0) {
        toast.success(
          `Card updated — $${res.settled_amount.toLocaleString()} paid and your plan is active again.`,
        );
      } else {
        toast.success(
          res.replaced_count > 0
            ? `Card updated — future charges will use ···· ${res.last4 ?? "the new card"}.`
            : "Card saved.",
        );
      }
    } catch (err) {
      // The card IS saved in Stripe at this point; only the swap failed.
      toast.error(err instanceof Error ? err.message : "Could not update your card. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const hasCard = Boolean(card?.has_card);
  const expiry = card?.exp_month && card?.exp_year
    ? `${String(card.exp_month).padStart(2, "0")}/${card.exp_year}`
    : null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading your card…
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-[1.3rem] border border-border/70 bg-card p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <CreditCard className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground">
                {hasCard
                  ? `${card?.brand ? titleCase(card.brand) : "Card"} ···· ${card?.last4}`
                  : "No card on file"}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {hasCard
                  ? <>Used for renewals and one-click unlocks{expiry ? ` · Expires ${expiry}` : ""}</>
                  : "Add a card to keep your plan renewing and unlock listings in one click."}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-gold-gradient px-4 py-2.5 text-sm font-bold text-primary-foreground shadow-sm transition-[filter] hover:brightness-[1.06]"
          >
            <CreditCard className="h-4 w-4" />
            {hasCard ? "Update card" : "Add card"}
          </button>
        </div>

        <div className="flex items-start gap-2.5 rounded-[1.3rem] border border-primary/25 bg-primary/10 px-4 py-3 text-[13px] leading-relaxed text-foreground/90">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p>
            Only one card is kept on file. Saving a new card replaces the current one —
            it becomes the card billed for your subscription right away, and the old card is removed.
          </p>
        </div>
      </div>

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
          style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          onClick={(e) => { if (e.target === e.currentTarget && !saving) setShowModal(false); }}
        >
          <div
            className="relative flex w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-card shadow-2xl animate-in fade-in zoom-in-95 duration-200"
            style={{ maxHeight: "calc(100vh / var(--app-zoom) - 3rem)" }}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-border px-5 pb-3.5 pt-4 sm:px-7">
              <BrandLogo className="h-7 w-auto object-contain" />
              <button
                onClick={() => { if (!saving) setShowModal(false); }}
                disabled={saving}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-muted disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
              <h3 className="text-lg font-extrabold leading-tight text-foreground">
                {hasCard ? "Update your card" : "Add your card"}
              </h3>
              {/* Deliberately does NOT promise "no charge today": a past_due buyer's
                  outstanding invoice is settled with this card as soon as it saves. */}
              <p className="mt-1 text-sm text-muted-foreground">
                {hasCard
                  ? <>Replaces {card?.brand ? titleCase(card.brand) : "the card"} ···· {card?.last4}. Saving it doesn't start a new charge.</>
                  : "Saving a card doesn't start a new charge — it's kept on file for your billing."}
                {" "}If a payment of yours failed, we'll settle it with this card right away.
              </p>

              <div className="mt-4 flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-3.5 py-2.5 text-xs text-muted-foreground">
                <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-500" />
                Card details go straight to Stripe — they never touch our servers.
              </div>

              <div className="mt-4">
                <StripePaymentElement
                  intentType="setup"
                  createIntent={createSetupIntent}
                  buttonLabel={hasCard ? "Save new card" : "Save card"}
                  onSuccess={handleSaved}
                  disabled={saving}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
